import { useEffect, useState } from "react";

export type PersistedSortItem = {
  field: string;
  sort: "asc" | "desc" | null | undefined;
};

type PaginationState = {
  page: number;
  pageSize: number;
  sortModel: readonly PersistedSortItem[];
};

type PaginationDefaults = Partial<PaginationState>;

const isValidPositiveInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

const isValidSortModel = (value: unknown): value is PersistedSortItem[] =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      item &&
      typeof item.field === "string" &&
      (item.sort === "asc" || item.sort === "desc" || item.sort == null),
  );

const getStoredPagination = (
  key: string,
  defaults: PaginationState,
): PaginationState => {
  try {
    const stored = localStorage.getItem(key);
    const parsed = stored ? JSON.parse(stored) : {};

    return {
      page: isValidPositiveInteger(parsed?.page) ? parsed.page : defaults.page,
      pageSize: isValidPositiveInteger(parsed?.pageSize)
        ? parsed.pageSize
        : defaults.pageSize,
      sortModel: isValidSortModel(parsed?.sortModel)
        ? parsed.sortModel
        : defaults.sortModel,
    };
  } catch {
    return defaults;
  }
};

export const usePersistedPagination = (
  key: string,
  defaults: PaginationDefaults = {},
) => {
  const normalizedDefaults: PaginationState = {
    page: isValidPositiveInteger(defaults.page) ? defaults.page : 1,
    pageSize: isValidPositiveInteger(defaults.pageSize) ? defaults.pageSize : 5,
    sortModel: isValidSortModel(defaults.sortModel) ? defaults.sortModel : [],
  };
  const initialState = getStoredPagination(key, normalizedDefaults);
  const [page, setPage] = useState(initialState.page);
  const [pageSize, setPageSize] = useState(initialState.pageSize);
  const [sortModel, setSortModel] = useState(initialState.sortModel);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify({ page, pageSize, sortModel }));
    } catch {
      // A paginação continua funcionando mesmo quando o storage não está disponível.
    }
  }, [key, page, pageSize, sortModel]);

  return { page, setPage, pageSize, setPageSize, sortModel, setSortModel };
};
